import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAllCollectedPIC } from "../../../../common/hooks/dataMapHooks";
import { SurveyAnswerFn, SurveyAnswers, useSurvey } from "../../../../common/hooks/api/useSurvey";
import { NetworkRequestState } from "../../../../common/models";
import { CategoryDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import {
  DataRetentionPolicyCategoryDto,
  DataRetentionPolicyDetailCategory,
  DataRetentionPolicyDto,
  RetentionOptions,
  UpdateDataRetentionPolicyDetailDto,
} from "../../../../common/service/server/dto/DataRetentionPolicyDto";
import { usePrimaryOrganizationId } from "../../../hooks";
import {
  CreateDataRentionPolicyFn,
  UpdateDataRentionPolicyFn,
  RemoveDataRetentionPolicyFn,
  useDataRetentionPolicy,
} from "../../../hooks/useDataRetention";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import { AlertCard, Button } from "@truevault/aquilo";
import { SurveyGateButton } from "../survey/SurveyGateButton";
import {
  DATA_RETENTION_CATEGORIES,
  DataRetentionCategoryQuestion,
  getDefaultDescription,
} from "./DataRetentionCategoryQuestion";
import { WYSIWYGEditor } from "../../../../common/components/input/WYSIWYGEditor";
import { DataRetentionCategoryQuestionDisabled } from "./DataRetentionCategoryQuestionDisabled";
import { isDetailComplete } from "./retentionPolicySteps";

export const DATA_RETENTION_SURVEY_NAME = "data-retention-survey";

function isEnabledRetentionDetail(
  cat:
    | DataRetentionPolicyCategoryDto
    | {
        builtinType: DataRetentionPolicyDetailCategory;
        isRemoved: boolean;
      },
): cat is DataRetentionPolicyCategoryDto {
  return (
    (
      cat as {
        builtinType: DataRetentionPolicyDetailCategory;
        isRemoved: boolean;
      }
    ).isRemoved !== true
  );
}

export type UseDataRetentionModule = {
  requests: {
    request: NetworkRequestState;
    dataMapRequest: NetworkRequestState;
    dataRetentionRequest: NetworkRequestState;
  };
  isFinished: boolean;
  // False until approvals have been read from a fetched policy; before that, isFinished is
  // computed from an empty approval set and reads as incomplete.
  isReady: boolean;
  disabledTooltip?: string;
  updateRequest: NetworkRequestState;
  createRequest: NetworkRequestState;
  removeRequest: NetworkRequestState;
  saveDefaultDescriptions: () => Promise<void>;
  props: DataRetentionModuleProps;
};

export const useDataRetentionModule = (doneSlug?: string): UseDataRetentionModule => {
  const orgId = usePrimaryOrganizationId();

  const { answers, setAnswer, request } = useSurvey(orgId, DATA_RETENTION_SURVEY_NAME);

  const { data: dataMap, request: dataMapRequest } = useDataMap(orgId);

  const {
    onLoad,
    result: dataRetention,
    request: dataRetentionRequest,
    refresh: refreshDataRetention,
    update: updateDataRetention,
    updateRequest,
    create: createDataRetention,
    createRequest,
    remove: removeDataRetention,
    removeRequest,
  } = useDataRetentionPolicy(orgId);

  const [approvedPurposes, setApprovedPurposes] = useState<Set<string>>(new Set());
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    if (onLoad) {
      setIsReady(true);
      setApprovedPurposes(() => {
        const res = new Set<string>();
        dataRetention?.details?.forEach((dto) => {
          if (dto.approved && (dto.retentionPeriod !== "CUSTOM" || dto.description)) {
            res.add(dto.id);
          }
        });
        return res;
      });
    }
  }, [onLoad]);

  const collectedPic = useAllCollectedPIC(dataMap);

  const unmappedItems = useMemo(() => {
    if (!dataRetention) {
      return [];
    }
    return collectedPic.filter(
      (pic) => !(dataRetention?.details ?? []).some((dr) => dr.pic.includes(pic.id)),
    );
  }, [collectedPic, dataRetention]);

  const hasDoneButton = Boolean(doneSlug);

  const doneButtonFinished = !hasDoneButton || answers[doneSlug] === "true";

  const isFinished = useMemo(() => {
    // The builder's per-purpose rule (isDetailComplete), so the Done button and dashboard can't
    // call a purpose finished while its step is still open — e.g. one approved before its last
    // category stopped being collected, which leaves it with no categories.
    const allApproved = dataRetention?.details?.every(
      (detail) => approvedPurposes.has(detail.id) && isDetailComplete(detail),
    );
    return allApproved && doneButtonFinished && unmappedItems.length === 0;
  }, [approvedPurposes, unmappedItems, doneButtonFinished, dataRetention]);

  const doneButton = hasDoneButton ? { slug: doneSlug } : undefined;
  let disabledTooltip = null;
  if (unmappedItems.length === 0) {
    disabledTooltip = "Save all retention periods before proceeding.";
  }

  const saveDefaultDescriptions = useCallback(async () => {
    const details = dataRetention?.details ?? [];
    const saves = details
      .filter((detail) => !detail.description && detail.builtinType)
      .map((detail) => {
        const defaultDescription = getDefaultDescription(detail);
        if (!defaultDescription) return null;
        return updateDataRetention({
          id: detail.id,
          dto: {
            description: defaultDescription,
            customLabel: detail.customLabel ?? null,
            retentionPeriod: detail.retentionPeriod,
            pic: detail.pic,
            approved: detail.approved,
          },
        });
      })
      .filter(Boolean);
    await Promise.all(saves);
  }, [dataRetention, updateDataRetention]);

  return {
    requests: {
      request,
      dataMapRequest,
      dataRetentionRequest,
    },
    isFinished,
    isReady,
    disabledTooltip,
    updateRequest,
    createRequest,
    removeRequest,
    saveDefaultDescriptions,
    props: {
      collectedPic,
      dataRetention,
      unmappedItems,
      updateDataRetention,
      createDataRetention,
      removeDataRetention,
      refreshDataRetention,
      answers,
      setAnswer,
      doneButton,
      setApproved: (purposeKey, approved) => {
        setApprovedPurposes((old) => {
          if (approved) {
            old.add(purposeKey);
          } else {
            old.delete(purposeKey);
          }
          return new Set(old);
        });
      },
    },
  };
};

export type DataRetentionSurveyDoneButton = {
  slug: string;
};
export type DataRetentionModuleProps = {
  collectedPic: CategoryDto[];
  dataRetention: DataRetentionPolicyDto;
  unmappedItems: CategoryDto[];
  updateDataRetention: UpdateDataRentionPolicyFn;
  createDataRetention: CreateDataRentionPolicyFn;
  removeDataRetention: RemoveDataRetentionPolicyFn;
  refreshDataRetention: () => Promise<unknown> | void;
  answers: SurveyAnswers;
  setAnswer: SurveyAnswerFn;
  doneButton?: DataRetentionSurveyDoneButton;
  setApproved: (retentionKey: string, approved: boolean) => void;
};
export const DataRetentionModule: React.FC<DataRetentionModuleProps> = ({
  collectedPic,
  dataRetention,
  answers,
  setAnswer,
  updateDataRetention,
  createDataRetention,
  removeDataRetention,
  unmappedItems,
  doneButton,
  setApproved,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const dataRetentionDetails = dataRetention?.details ?? [];
  // Intro statement: explicit save, like the legacy survey control it replaces, so a half-typed
  // sentence never lands on the draft policy.
  const savedIntro = answers["retention-policy"] ?? "";
  const [introDraft, setIntroDraft] = useState<string>(savedIntro);
  const [isSavingIntro, setIsSavingIntro] = useState(false);

  const sortedDetails: (
    | DataRetentionPolicyCategoryDto
    | {
        builtinType: DataRetentionPolicyDetailCategory;
        isRemoved: boolean;
      }
  )[] = [
    ...Object.keys(DATA_RETENTION_CATEGORIES)
      .filter(
        (cat) =>
          !dataRetentionDetails.some((retentionCategory) => cat == retentionCategory.builtinType),
      )
      .map((builtinType) => ({
        builtinType: builtinType as DataRetentionPolicyDetailCategory,
        isRemoved: true,
      })),
    ...dataRetentionDetails,
  ].sort((a, b) => {
    const left = DATA_RETENTION_CATEGORIES[a.builtinType]?.displayOrder ?? 1000;
    const right = DATA_RETENTION_CATEGORIES[b.builtinType]?.displayOrder ?? 1000;
    return left - right;
  });

  const warningCallout = (
    <>
      {unmappedItems.length > 0 && (
        <AlertCard variant="warning" containerClasses="!bg-amber-100">
          <div>
            <div className="font-semibold mb-1">
              The following personal information categories were not selected for a retention
              policy. To continue, make sure every category is covered by at least one retention
              purpose above.
            </div>
            <ul className="list-disc pl-[20px]">
              {unmappedItems.map((unmappedPic) => (
                <li key={unmappedPic.id}>{unmappedPic.name}</li>
              ))}
            </ul>
          </div>
        </AlertCard>
      )}
    </>
  );

  return (
    <>
      <div className="bg-white border border-solid border-neutral-200 rounded-xl shadow-[0px_1px_2px_0px_#1018280D] overflow-hidden">
        <div className="bg-neutral-0 border-b border-solid border-neutral-200 px-[24px] py-[11px] text-[16px] font-medium text-neutral-900">
          Intro statement
        </div>
        <div className="p-[24px]">
          <div className="mb-[8px] text-[14px] leading-[20px] text-neutral-500">
            Opens the retention policy section of your privacy policy, ahead of the purposes below.
          </div>
          <WYSIWYGEditor
            height="160px"
            rows={3}
            toolbar={false}
            defaultValue={savedIntro}
            onChange={setIntroDraft}
          />
          <div className="mt-[12px] flex justify-end">
            <Button
              color="primary"
              size="s"
              disabled={isSavingIntro || introDraft === savedIntro}
              onClick={async () => {
                setIsSavingIntro(true);
                try {
                  await setAnswer("retention-policy", introDraft);
                } finally {
                  setIsSavingIntro(false);
                }
              }}
            >
              {isSavingIntro ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-[24px]">
        {sortedDetails.map((detail) => {
          if (isEnabledRetentionDetail(detail)) {
            return (
              <DataRetentionCategoryQuestion
                key={detail.id}
                picOptions={collectedPic}
                detail={detail}
                onUpdate={(dto: UpdateDataRetentionPolicyDetailDto) => {
                  updateDataRetention({
                    id: detail.id,
                    dto,
                  });
                }}
                setApproved={setApproved}
                onRemove={() => {
                  removeDataRetention({ id: detail.id });
                }}
              />
            );
          } else {
            const canSetRetention =
              DATA_RETENTION_CATEGORIES[detail.builtinType].description?.includes("YEARS");
            const defaultRetentionPeriod: RetentionOptions = canSetRetention ? "YEARS_7" : "CUSTOM";
            return (
              <DataRetentionCategoryQuestionDisabled
                key={detail.builtinType}
                category={detail.builtinType}
                onRestore={() => {
                  createDataRetention({
                    dto: {
                      builtinType: detail.builtinType,
                      description: "",
                      customLabel: null,
                      retentionPeriod: defaultRetentionPeriod,
                      pic: [],
                      approved: false,
                    },
                  });
                }}
              />
            );
          }
        })}

        {isAdding && (
          <DataRetentionCategoryQuestion
            picOptions={collectedPic}
            detail={null}
            onUpdate={() => {}}
            onCreate={(dto) => {
              createDataRetention({ dto });
            }}
            onCreated={() => setIsAdding(false)}
          />
        )}

        {doneButton ? (
          <>
            <SurveyGateButton
              slug={doneButton.slug}
              survey={answers}
              updateResponse={setAnswer}
              label="Done with Review"
            >
              <div className="mb-[16px]">{warningCallout}</div>
            </SurveyGateButton>
            <Button color="primary" size="s" disabled={isAdding} onClick={() => setIsAdding(true)}>
              Add Retention Purpose
            </Button>
          </>
        ) : (
          <>
            <div className="mb-[16px]">{warningCallout}</div>
            <Button color="primary" size="s" disabled={isAdding} onClick={() => setIsAdding(true)}>
              Add Retention Purpose
            </Button>
          </>
        )}
      </div>
    </>
  );
};
